/**
 * The question statement, given data, figures and (for open problems) the part
 * list (brief §practice: "given block always visible, figures"). The hints are
 * the session page's `HintLadder` (pedagogy spec §4.3.5).
 */
import type { PublicQuestion } from "@shared/types";
import { Figure } from "@client/components/Figure";
import { Localized } from "@client/components/Localized";
import { QuestionParts } from "@client/components/QuestionParts";
import { ReportIssue } from "@client/components/ReportIssue";
import { SourceBadge, TranslationPendingBadge } from "@client/components/SourceBadge";
import { Badge, Card } from "@client/components/ui";
import { useT } from "@client/i18n";
import { formatPoints } from "@client/lib/format";
import { useLang } from "@client/i18n";
import { TopicChip } from "./TopicChip";

export interface QuestionCardProps {
  question: PublicQuestion;
  /**
   * Show the topic + Y&F chip above the statement (P1-4). Only where the topic is no secret: a
   * topic or lecture set. A mixed batch asks the student to name the principle first, so the
   * chip would answer that; there the topic is shown with the solution instead.
   */
  showTopic?: boolean;
}

const DIFFICULTY_TONE = ["neutral", "accent", "warning"] as const;

export function QuestionCard({ question, showTopic = false }: QuestionCardProps) {
  const t = useT();
  const { lang } = useLang();
  const bodyFigures = question.figures.filter((f) => !f.partId);

  return (
    <Card
      title={t(`practice.type.${question.type}`)}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <SourceBadge source={question.source} />
          <Badge tone={DIFFICULTY_TONE[question.difficulty - 1]}>
            {t(`practice.difficulty.${question.difficulty}`)}
          </Badge>
          <Badge tone="neutral">{formatPoints(question.points, lang)}</Badge>
        </div>
      }
    >
      {showTopic && <TopicChip topicId={question.topic} />}
      <TranslationPendingBadge pending={question.translationPending} className="mb-2" />
      <Localized value={question.statement} />

      {question.given.length > 0 && (
        <div className="mt-4 space-y-2 rounded-lg bg-ink-100 p-3 text-sm dark:bg-ink-800/60">
          {question.given.map((item, i) => (
            <Localized key={i} value={item} variant="compact" />
          ))}
        </div>
      )}

      {bodyFigures.map((figure, i) => (
        <Figure key={i} figure={figure} />
      ))}

      {/* Numeric parts print their prompt above their own input in
          `AnswerArea`; open questions have no per-part control, so the
          sub-questions are listed here or they are never shown at all. */}
      {question.type === "open" && (
        <QuestionParts parts={question.parts} figures={question.figures} />
      )}

      <ReportIssue questionId={question.id} className="mt-4" />
    </Card>
  );
}
